/**
 * Per-domain build config for the LogPulse marketing site.
 *
 * Each ccTLD is a separate Cloudflare Pages project that builds the same
 * source with VITE_SITE_LOCALE set. Marketing pages live at root paths on
 * every domain (no /nl/, /fr/, /de/ prefix). Docs and blog are EN-only and
 * served exclusively from logpulse.io, ccTLDs link to .io for those paths
 * and 301-redirect any incoming /docs or /blog requests via _redirects.
 */

export const LOCALES = ['en', 'nl', 'fr', 'de', 'es', 'it'] as const;
export type Locale = (typeof LOCALES)[number];

const PROD_LOCALE_ORIGINS: Record<Locale, string> = {
  en: 'https://logpulse.io',
  nl: 'https://logpulse.nl',
  de: 'https://logpulse.de',
  fr: 'https://logpulse.fr',
  es: 'https://logpulse.es',
  it: 'https://logpulse.it',
};

/**
 * In dev every locale runs as its own Vite server on localhost (started by
 * start-dev.sh: one port per locale, sequential from the base port). The
 * script passes its actual locale list + base port via VITE_WEBSITE_LOCALES /
 * VITE_WEBSITE_BASE_PORT so this mapping can never drift from the ports it
 * chose; the defaults below mirror the script's defaults for a standalone
 * `pnpm dev:website`. A locale that isn't running locally falls back to its
 * production domain so the language selector never links to a dead port.
 */
const DEV_LOCALE_ORDER: string[] = (
  (import.meta.env?.VITE_WEBSITE_LOCALES as string | undefined) ?? 'en nl de fr es it'
).split(/\s+/);
const DEV_BASE_PORT = Number(import.meta.env?.VITE_WEBSITE_BASE_PORT ?? 5175);

function devOrigin(locale: Locale): string {
  const idx = DEV_LOCALE_ORDER.indexOf(locale);
  if (idx === -1) return PROD_LOCALE_ORIGINS[locale];
  return `http://localhost:${DEV_BASE_PORT + idx}`;
}

export const LOCALE_ORIGINS: Record<Locale, string> = import.meta.env?.DEV
  ? (Object.fromEntries(LOCALES.map((l) => [l, devOrigin(l)])) as Record<Locale, string>)
  : PROD_LOCALE_ORIGINS;

export const HREFLANG_TAGS: Record<Locale, string> = {
  en: 'en',
  nl: 'nl-NL',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
};

/**
 * Additional hreflang tags for same-language neighbouring markets, mapped to
 * the locale site that serves them: Belgian Dutch → logpulse.nl, Austrian and
 * Swiss German → logpulse.de. The logpulse.at and logpulse.ch domains are
 * brand-protection redirects to logpulse.de (see scripts/generate-seo.ts), so
 * the German site remains the canonical target for those visitors. Without
 * these tags, search engines get no signal that those visitors are well served
 * on the existing domains.
 */
export const EXTRA_HREFLANGS: Record<string, Locale> = {
  'nl-BE': 'nl',
  'de-AT': 'de',
  'de-CH': 'de',
};

export const OG_LOCALE_TAGS: Record<Locale, string> = {
  en: 'en_US',
  nl: 'nl_NL',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  it: 'it_IT',
};

const RAW_LOCALE = (import.meta.env?.VITE_SITE_LOCALE ?? 'en') as string;
export const SITE_LOCALE: Locale = (LOCALES as readonly string[]).includes(RAW_LOCALE)
  ? (RAW_LOCALE as Locale)
  : 'en';

export const SITE_ORIGIN = LOCALE_ORIGINS[SITE_LOCALE];

/** Contact form endpoint: Cloudflare Worker that mails via Microsoft Graph.
 *  Override per-environment with VITE_CONTACT_API_URL; defaults to the
 *  production custom route on forms.logpulse.io. We keep marketing-form
 *  endpoints on a separate subdomain from the product API (api.logpulse.io)
 *  so they can scale independently and share form-specific CORS rules. */
export const CONTACT_API_URL: string =
  (import.meta.env?.VITE_CONTACT_API_URL as string | undefined) ?? 'https://forms.logpulse.io/contact';

/** Community Discord.
 *  Replace the REPLACE_ME placeholder below with the real invite (e.g.
 *  https://discord.gg/abcd1234) once the server is live, or set
 *  VITE_DISCORD_INVITE_URL at build time. Until a real invite is set,
 *  DISCORD_LIVE stays false and the community page + footer show a
 *  "launching soon" state instead of linking to a dead invite. */
export const DISCORD_INVITE_URL: string =
  (import.meta.env?.VITE_DISCORD_INVITE_URL as string | undefined) ?? 'https://discord.gg/REPLACE_ME';
export const DISCORD_LIVE: boolean = !DISCORD_INVITE_URL.includes('REPLACE_ME');

/**
 * Locales whose domain serves its own /guides section. Kept as a literal
 * (NOT derived from the guides registry) so this module — which is in the
 * main bundle — never imports the ~50 KB guide data. When you add the first
 * guide in a new locale to src/data/guides-data.ts, add the locale here too.
 */
export const GUIDE_LOCALES: Locale[] = ['en', 'nl', 'de', 'fr', 'es', 'it'];

function isGuidesPath(path: string): boolean {
  return path === '/guides' || path.startsWith('/guides/');
}

/** Paths that only exist on the EN site (logpulse.io). Shared resources:
 *  no need to fragment docs, blog, or community across 4 ccTLDs. Guides are
 *  locale-aware: a domain in GUIDE_LOCALES serves its own guides (English-
 *  only guide articles on a ccTLD 301 to .io via _redirects). */
export function isEnOnlyPath(path: string): boolean {
  if (isGuidesPath(path)) return !GUIDE_LOCALES.includes(SITE_LOCALE);
  return (
    path === '/docs' || path.startsWith('/docs/') ||
    path === '/blog' || path.startsWith('/blog/') ||
    path === '/community' || path.startsWith('/community/') ||
    path === '/alternatives' || path.startsWith('/alternatives/')
  );
}

/** Marketing paths that get hreflang alternates and exist on every locale domain. */
export function isMarketingPath(path: string): boolean {
  if (path === '/') return true;
  // Guides exist per-locale with differing slugs, never on all four domains —
  // GuidesPage/GuidePage emit their own hreflang instead of the 4-domain set.
  if (isGuidesPath(path)) return false;
  if (isEnOnlyPath(path)) return false;
  const cleanPath = path.split('#')[0];
  // Top-level slug (e.g. /security, /compare)
  if (/^\/[a-z][a-z0-9-]*$/i.test(cleanPath)) return true;
  // /compare/<duo-slug>
  if (/^\/compare\/[a-z0-9-]+$/i.test(cleanPath)) return true;
  // /compliance/<framework> (e.g. /compliance/nis2, /compliance/dora)
  if (/^\/compliance\/[a-z0-9-]+$/i.test(cleanPath)) return true;
  // /integrations/<product> marketing pages (e.g. /integrations/topdesk)
  if (/^\/integrations\/[a-z0-9-]+$/i.test(cleanPath)) return true;
  return false;
}

/**
 * Resolve an internal href to a navigable target for the current build.
 * - Same-domain marketing paths → unchanged (relative)
 * - Docs/blog paths on a non-EN build → absolute URL to logpulse.io
 * - External / hash-only / non-internal → unchanged
 */
export function resolveHref(href: string): string {
  if (!href.startsWith('/')) return href;
  if (SITE_LOCALE !== 'en' && isEnOnlyPath(href.split('#')[0])) {
    return `${LOCALE_ORIGINS.en}${href}`;
  }
  return href;
}

/** Absolute URL on a target locale's domain for the same path. */
export function urlOnLocale(path: string, locale: Locale): string {
  const cleanPath = path === '/' ? '' : path;
  return `${LOCALE_ORIGINS[locale]}${cleanPath}`;
}

/**
 * Per-page Open Graph image, by convention: the build renders one card per
 * route into /og/<slug>.jpg (scripts/generate-og-images.ts). The slug rule
 * here MUST match slugForPath in that script. The homepage keeps the bespoke
 * /og-image.jpg, which is also the fallback for anything unroutable.
 */
export function ogImageUrlForPath(pathname: string): string {
  const clean = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (clean === '/' || clean === '') return `${SITE_ORIGIN}/og-image.jpg`;
  return `${SITE_ORIGIN}/og/${clean.replace(/^\//, '').replace(/\//g, '-')}.jpg`;
}
